<template>
  <div class="input-wrapper-custom-material">
    <div class="group">
      <imask-input
        :required="field.isRequired"
        v-model="fieldValue"
        :disabled="disabled"
        :readonly="field.isReadOnly"
        :mask="Number"
        :thousandsSeparator="userThouSep"
        :scale="field.validations.scale"
        :radix="userDecSep"
        :unmask="true"
        class="mdl-textfield__input"
        :class="{ 'is-invalid': fieldErrors.length > 0, 'dirty': validator.document.fields.$each[index].value.$dirty }"
        :hint="field.observations"
        @change="onChange"
        @input="validator.document.fields.$each[index].value.$touch()"
        @blur="validator.document.fields.$each[index].value.$touch()"
        placeholder=" "
      />
      <span class="highlight"></span>
      <span
        class="bar"
        :class="{ 'is-invalid': fieldErrors.length > 0 }"
      ></span>
      <label
        class="mdl-textfield__label v-label theme--light"
        :class="{ 'is-invalid': fieldErrors.length > 0 }"
        >{{ labelText }}</label
      >
    </div>
    <div v-if="fieldErrors.length > 0" class="text-field__error">
      {{ fieldErrors[0] }}
    </div>
  </div>
</template>
<script>
import { IMaskComponent } from 'vue-imask';
import { EventBus } from '@/eventBus.js';

export default {
    data() {
        return {
            userDecSep: this.$store.getters['account/user'].MetricDecimalSep,
            userThouSep: this.$store.getters['account/user'].MetricThousandSep,
            firstTime: true
        };
    },
    props: {
        field: {
            type: Object,
            required: true,
        },
        validator: {
            type: Object,
            default: () => ({}),
        },
        index: {
            type: Number,
        },
        ctIndex: {
            type: Number,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        fieldChangeSource: {
            type: Number,
            default: 0
        }
    },
    components: {
        'imask-input': IMaskComponent,
    },
    computed: {
        labelText() {
            return `${this.field.title}${this.field.isRequired ? ' *' : ''}`;
        },
        fieldValue: {
            get() {
                try {
                    this.formatNumberString(this.field.value, this.field.validations.scale);
                } catch (ex) {
                    //Ignore error
                }
                return this.field.value;
            },
            set(newValue) {
                this.field.value = newValue;
            },
        },
        fieldErrors() {
            const errors = [];

            if (this.validator.document) {
                const field = this.validator.document.fields.$each[this.index];

                if (!field.value.$dirty) {
                    return errors;
                }

                if (!field.value.required && !field.$model.value) {
                    errors.push(this.$t('RequiredField'));
                }

                if (!field.value.minValue || !field.value.maxValue) {
                    errors.push(this.$t('NumericFieldValidationError'));
                }

            } else if (this.validator.contentTypeList) {
                const field = this.validator.contentTypeList.$each[this.ctIndex].document.fields.$each[this.index];

                if (!field.value.$dirty) {
                    return errors;
                }

                if (!field.value.required && !field.$model.value) {
                    errors.push(this.$t('RequiredField'));
                }
            }
            return errors;
        },
    },
    methods: {
        onChange() {
            EventBus.$emit('field-edited', this.field, this.fieldChangeSource);

            this.field.valueNumber = this.field.value;
        },
        formatNumberString(numberString, scale) {
            // Check if the number string contains a decimal point
            const decimalIndex = numberString.indexOf('.');

            if (decimalIndex !== -1) {
                // Calculate the current number of decimal places
                const currentDecimalPlaces = numberString.length - decimalIndex - 1;

                // Only modify the string if the scale is less than the current number of decimal places
                if (scale < currentDecimalPlaces) {
                    // Truncate the string to the desired scale
                    let modifiedValue;
                    if (scale === 0) {
                        modifiedValue = numberString.substring(0, decimalIndex + scale);
                    } else {
                        modifiedValue = numberString.substring(0, decimalIndex + scale + 1);
                    }

                    this.field.value = modifiedValue;
                }
            }

            this.field.valueNumber = parseFloat(this.field.value);
        }
    },
};
</script>
<style lang="scss" scoped>
$bg-color: #424242;
$hl-color: #00cae3;
$muted-color: mix(white, $bg-color, 70%);
$unfocused-color: rgb(118, 118, 118);
$invalid-color: rgb(255, 87, 86);
$trans-time: 300ms;
$width: 100%;

.input-wrapper-custom-material {
  position: relative;
  display: flex;
  align-items: center;
  .group {
    flex: 1;
    position: relative;
    margin: 14px 0 10px 0;
  }
  // INPUTS
  .mdl-textfield__input {
    background: none;
    color: inherit;
    accent-color: #00cae3;
    line-height: 12px;
    font-size: 14px;
    padding: 8px 0;
    display: block;
    width: $width;
    border: none;
    border-radius: 0;
    border-bottom: 1px solid $muted-color;
    &:focus {
      outline: none;
    }
    &:required {
      &:focus ~ label,
      &:valid ~ label {
        top: -10px;
        font-size: 10px;
        color: $hl-color;
      }
      &:not(:focus) ~ label {
        color: $unfocused-color;
      }
      &:invalid.dirty ~ label {
        color: $invalid-color;
      }
    }
    &:not(:required) {
      &:focus ~ label,
      &:not(:placeholder-shown):valid ~ label {
        top: -10px;
        font-size: 10px;
        color: $hl-color;
      }
      &:not(:focus) ~ label,
      &:not(:placeholder-shown):valid:not(:focus) ~ label {
        color: $unfocused-color;
      }
    }
    &:focus ~ .bar:before {
      width: $width;
    }
  }
  label {
    font-size: 14px;
    position: absolute;
    pointer-events: none;
    top: 10px;
    transition: $trans-time ease all;
    color: $unfocused-color;
    font-weight: 300;
  }
  .bar {
    position: relative;
    display: block;
    width: $width;
    &:before {
      content: "";
      height: 2px;
      width: 0;
      bottom: 0px;
      position: absolute;
      background: $hl-color;
      transition: $trans-time ease all;
      left: 0%;
    }
    &.is-invalid:before {
      background: $invalid-color;
    }
  }
  .text-field__error {
    color: $invalid-color;
    font-size: 12px;
    line-height: 12px;
    word-break: break-word;
    word-wrap: break-word;
    position: absolute;
    top: 100%;
  }
  .is-invalid {
    color: $invalid-color;
  }
}
</style>
